import React, { useEffect, useState } from 'react';

import { TextField } from '@mui/material';

import lensContract from '~/types/lensContract.json';

import { Choice, Row } from './settingsKit';
import type { Block } from './types';

type TimeAxisSettingsProps = { block: Block; pending: boolean; onChange: (patch: Record<string, unknown>) => void };

const RANGE_LABELS: Record<string, string> = { period: 'Full selected days', data: 'Available data' };
const intervalLabel = (minutes: number) =>
  minutes % 60 === 0
    ? `${minutes / 60} hour${minutes === 60 ? '' : 's'}`
    : `${minutes} minute${minutes === 1 ? '' : 's'}`;

export default function TimeAxisSettings({ block, pending, onChange }: TimeAxisSettingsProps) {
  const saved = typeof block.xTick === 'number' ? String(block.xTick) : '';
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [saved]);
  const customValue = typeof block.xTick === 'number' && !lensContract.timeTickPresets.includes(block.xTick);
  const invalid = draft.trim() !== '' && (!Number.isSafeInteger(Number(draft)) || Number(draft) < 1);
  const commit = () => {
    if (invalid || draft.trim() === saved || (draft.trim() !== '' && Number(draft) === block.xTick)) return;
    onChange({ xTick: draft.trim() === '' ? null : Number(draft) });
    if (draft.trim() === '') setCustom(false);
  };
  if (!lensContract.timeChartKinds.some((kind) => kind in block)) return null;
  return (
    <>
      <Choice
        choices={[
          ['auto', 'Automatic'],
          ...lensContract.timeRangeModes.map((mode): [string, string] => [mode, RANGE_LABELS[mode]]),
        ]}
        disabled={pending}
        inherit="auto"
        label="Time range"
        onChange={(next) => onChange({ xRange: next === 'auto' ? null : next })}
        value={typeof block.xRange === 'string' ? block.xRange : 'auto'}
      />
      <Choice
        choices={[
          ['auto', 'Automatic'],
          ...lensContract.timeTickPresets.map((minutes): [string, string] => [String(minutes), intervalLabel(minutes)]),
          ['custom', 'Custom…'],
        ]}
        disabled={pending}
        inherit="auto"
        label="Tick spacing"
        onChange={(next) => {
          setCustom(next === 'custom');
          if (next === 'custom') {
            setDraft(saved);
          } else {
            onChange({ xTick: next === 'auto' ? null : Number(next) });
          }
        }}
        value={custom || customValue ? 'custom' : saved || 'auto'}
      />
      {custom || customValue ? (
        <Row label="Custom tick spacing" set={Boolean(saved)}>
          <TextField
            disabled={pending}
            error={invalid}
            fullWidth
            helperText={invalid ? 'Enter a positive whole number of minutes.' : undefined}
            inputProps={{ 'aria-label': 'Tick interval in minutes', inputMode: 'numeric', min: 1, step: 1 }}
            onBlur={commit}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                (event.target as HTMLInputElement).blur();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setDraft(saved);
              }
            }}
            placeholder="Automatic"
            size="small"
            type="number"
            value={draft}
            variant="standard"
          />
        </Row>
      ) : null}
    </>
  );
}
